import { CaretLeft, CaretRight, Info } from '@phosphor-icons/react';
import { NavLink } from 'react-router';
import { cn } from '@/lib/cn';
import { NAV_SECTIONS } from '@/app/router/routes';
import { IconButton } from '@/components/primitives/icon-button';
import { Tooltip } from '@/components/primitives/tooltip';

interface SidebarNavigationProps {
  collapsed: boolean;
  onToggle: () => void;
}

export function SidebarNavigation({ collapsed, onToggle }: SidebarNavigationProps) {
  return (
    <aside
      data-collapsed={collapsed}
      className={cn(
        'flex h-full shrink-0 flex-col gap-4 py-4 pl-4',
        'transition-[width] duration-400 ease-[var(--ease-gentle)]',
        collapsed ? 'w-[var(--spacing-sidebar-collapsed)] pr-3' : 'w-[var(--spacing-sidebar)] pr-2',
      )}
    >
      <div className="flex h-11 items-center gap-2 pl-2">
        <span
          aria-hidden="true"
          className="flex size-8 shrink-0 items-center justify-center rounded-[0.9rem] bg-accent-soft"
        >
          <span className="size-3 rounded-full bg-accent" />
        </span>
        {!collapsed && (
          <span className="min-w-0 leading-tight">
            <span className="block truncate text-[0.9375rem] font-semibold tracking-tight">
              GentleTask
            </span>
            <span className="block truncate text-[0.6875rem] tracking-[0.14em] text-text-muted uppercase">
              Secretary&apos;s Desk
            </span>
          </span>
        )}
      </div>

      <nav aria-label="主要導覽" className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto">
        {NAV_SECTIONS.map((section) => (
          <div key={section.id} className="flex flex-col gap-1">
            {!collapsed && (
              <span className="px-3 pb-1 text-[0.625rem] font-medium tracking-[0.18em] text-text-muted uppercase">
                {section.label}
              </span>
            )}
            {section.items.map((item) => {
              const Icon = item.icon;
              const link = (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    cn(
                      'group relative flex h-10 items-center gap-3 rounded-full',
                      'transition-[background-color,color] duration-300 ease-[var(--ease-gentle)]',
                      collapsed ? 'w-10 justify-center' : 'px-3',
                      isActive
                        ? 'bg-accent-soft font-medium text-text'
                        : 'text-text-muted hover:bg-surface hover:text-text',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon
                        size={20}
                        weight={isActive ? 'fill' : 'regular'}
                        className="shrink-0"
                        aria-hidden="true"
                      />
                      <span className={cn('truncate text-[0.9375rem]', collapsed && 'sr-only')}>
                        {item.title}
                      </span>
                    </>
                  )}
                </NavLink>
              );

              return collapsed ? (
                <Tooltip key={item.path} label={item.title} side="right">
                  {link}
                </Tooltip>
              ) : (
                link
              );
            })}
          </div>
        ))}
      </nav>

      <div
        className={cn(
          'flex items-center',
          collapsed ? 'flex-col gap-1' : 'justify-between gap-2 pl-3 pr-1',
        )}
      >
        <AboutLink collapsed={collapsed} />
        <IconButton
          label={collapsed ? '展開導覽列' : '收合導覽列'}
          tooltipSide="right"
          onClick={onToggle}
          icon={collapsed ? <CaretRight size={18} /> : <CaretLeft size={18} />}
        />
      </div>
    </aside>
  );
}

/** 作者的部落格。刻意小而安靜，不跟主要導覽搶注意力。 */
const AUTHOR_BLOG_URL = 'https://yicheng-yvonne.com/';

function AboutLink({ collapsed }: { collapsed: boolean }) {
  const link = (
    <a
      href={AUTHOR_BLOG_URL}
      target="_blank"
      rel="noreferrer noopener"
      aria-label="關於：前往作者的部落格（另開新分頁）"
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full text-[0.75rem] text-text-muted',
        'transition-colors duration-200 hover:text-accent',
        collapsed ? 'size-8 justify-center' : 'px-1 py-1',
      )}
    >
      <Info size={14} aria-hidden="true" />
      <span className={cn(collapsed && 'sr-only')}>關於</span>
    </a>
  );

  return collapsed ? (
    <Tooltip label="關於（作者的部落格）" side="right">
      {link}
    </Tooltip>
  ) : (
    link
  );
}
